import logger from "../logger";
import tokenManager from "../taskpane/services/tokenManager";
import { managedFetch } from "./requestManager";

const logCfDiag = process.env.NODE_ENV !== "production";

/**
 * Reads non-sensitive config from OfficeRuntime.storage first, then localStorage.
 * @param {string} key
 * @returns {Promise<string | null>}
 */
async function getConfigStorageItem(key) {
  if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
    return await OfficeRuntime.storage.getItem(key);
  }
  if (typeof localStorage !== "undefined") {
    return localStorage.getItem(key);
  }
  return null;
}

export const STORAGE_KEYS = {
  SELECTED_ORG_ID: "selectedOrganizationId",
};

export async function checkAuthentication() {
  return tokenManager.checkAuthentication();
}

export async function getAccessToken() {
  return tokenManager.getAccessToken();
}

export async function getSelectedOrganizationId() {
  try {
    let selectedOrgId = null;
    let source = "none";

    if (typeof OfficeRuntime !== "undefined" && OfficeRuntime?.storage) {
      selectedOrgId = await OfficeRuntime.storage.getItem(STORAGE_KEYS.SELECTED_ORG_ID);
      if (selectedOrgId) source = "OfficeRuntime";
    }

    if (!selectedOrgId) {
      selectedOrgId = await getConfigStorageItem(STORAGE_KEYS.SELECTED_ORG_ID);
      if (selectedOrgId) source = "configStorage";
    }

    if (logCfDiag) {
      logger.info(
        "[DIAG:CF:getOrgId] orgId found=%s, source=%s, value=%s",
        !!selectedOrgId,
        source,
        selectedOrgId || "null"
      );
    }
    return selectedOrgId || null;
  } catch (error) {
    if (logCfDiag) {
      logger.error("[DIAG:CF:getOrgId] Exception: %s", error.message);
    }
    return null;
  }
}

export async function getSelectedOrganizationUnitId() {
  try {
    let orgUnitId = null;
    let source = "none";

    if (typeof OfficeRuntime !== "undefined" && OfficeRuntime?.storage) {
      orgUnitId = await OfficeRuntime.storage.getItem("selectedOrganizationUnitId");
      if (orgUnitId) source = "OfficeRuntime";
    }

    if (!orgUnitId) {
      orgUnitId = await getConfigStorageItem("selectedOrganizationUnitId");
      if (orgUnitId) source = "configStorage";
    }

    if (logCfDiag) {
      logger.info(
        "[DIAG:CF:getOrgUnitId] orgUnitId found=%s, source=%s, value=%s",
        !!orgUnitId,
        source,
        orgUnitId || "null"
      );
    }
    return orgUnitId || null;
  } catch (error) {
    if (logCfDiag) {
      logger.error("[DIAG:CF:getOrgUnitId] Exception: %s", error.message);
    }
    return null;
  }
}

export async function getApiBaseUrl() {
  try {
    let environment = null;
    let envSource = "default";

    if (typeof OfficeRuntime !== "undefined" && OfficeRuntime?.storage) {
      environment = await OfficeRuntime.storage.getItem("selectedEnvironment");
      if (environment) envSource = "OfficeRuntime";
    }

    if (!environment) {
      environment = await getConfigStorageItem("selectedEnvironment");
      if (environment) envSource = "configStorage";
    }

    const defaultEnv = process.env.NODE_ENV === "production" ? "production" : "staging";
    environment = environment || defaultEnv;

    const envUrls = {
      local: process.env.LOCAL_API_BASE_URL,
      staging: process.env.STAG_API_BASE_URL,
      production: process.env.PROD_API_BASE_URL,
    };

    const baseUrl = envUrls[environment];

    if (logCfDiag) {
      logger.info(
        "[DIAG:CF:getApiBaseUrl] env=%s, source=%s, NODE_ENV=%s, defaultEnv=%s, resolvedUrl=%s",
        environment,
        envSource,
        process.env.NODE_ENV,
        defaultEnv,
        baseUrl || "NOT CONFIGURED"
      );
    }

    if (!baseUrl) {
      if (logCfDiag) {
        logger.error(
          "[DIAG:CF:getApiBaseUrl] API Base URL not configured for environment: %s",
          environment
        );
      }
      return null;
    }

    return baseUrl;
  } catch (error) {
    if (logCfDiag) {
      logger.error("[DIAG:CF:getApiBaseUrl] Exception: %s", error.message);
    }
    return null;
  }
}

export function getNestedProperty(obj, path) {
  if (!obj || typeof obj !== "object") {
    return undefined;
  }
  const parts = path.split(".");
  let current = obj;
  for (let i = 0; i < parts.length; i++) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[parts[i]];
  }
  return current;
}

export function formatForExcel(value) {
  if (value === null || value === undefined) {
    return "#N/A";
  }

  if (Array.isArray(value)) {
    return value
      .map(function (item) {
        if (item === null || item === undefined) {
          return "";
        }
        return String(item);
      })
      .join(", ");
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  return String(value);
}

/**
 * Common fetch logic for all Novata formulas.
 */
export async function fetchNovataData({
  endpoint,
  queryParams = {},
  requiredParams = {},
  attributeMap = {},
  fieldName,
  postProcess = null,
}) {
  const callId = Math.random().toString(36).substring(2, 8);
  try {
    if (logCfDiag) {
      logger.info(
        "[DIAG:CF:fetch:%s] START endpoint=%s, fieldName=%s, queryParams=%s",
        callId,
        endpoint,
        fieldName,
        JSON.stringify(queryParams)
      );
    }

    for (const [name, value] of Object.entries(requiredParams)) {
      if (value === null || value === undefined || value === "") {
        if (logCfDiag) {
          logger.warn("[DIAG:CF:fetch:%s] Missing required param: %s", callId, name);
        }
        return `#ERROR: ${name} is required.`;
      }
    }

    const authCheck = await checkAuthentication();
    if (!authCheck.authenticated) {
      if (logCfDiag) {
        logger.error("[DIAG:CF:fetch:%s] Auth check FAILED: %s", callId, authCheck.error);
      }
      return "#ERROR: " + (authCheck.error || "Please login through the Novata task pane.");
    }
    if (logCfDiag) {
      logger.info("[DIAG:CF:fetch:%s] Auth check passed", callId);
    }

    const accessToken = await getAccessToken();
    const selectedOrgId = await getSelectedOrganizationId();

    if (!accessToken) {
      if (logCfDiag) {
        logger.error(
          "[DIAG:CF:fetch:%s] getAccessToken() returned null despite auth check passing",
          callId
        );
      }
      return "#ERROR: Authentication required.";
    }
    if (!selectedOrgId) {
      if (logCfDiag) {
        logger.error("[DIAG:CF:fetch:%s] No organization selected", callId);
      }
      return "#ERROR: No organization selected.";
    }

    const API_BASE_URL = await getApiBaseUrl();
    if (!API_BASE_URL) {
      if (logCfDiag) {
        logger.error("[DIAG:CF:fetch:%s] API base URL not configured", callId);
      }
      return "#ERROR: API Base URL not configured.";
    }

    let fullEndpoint = API_BASE_URL + endpoint;
    const searchParams = new URLSearchParams();
    for (const [key, val] of Object.entries(queryParams)) {
      if (val !== undefined && val !== null && val !== "") {
        searchParams.append(key, val.toString().trim());
      }
    }

    if (searchParams.toString()) {
      fullEndpoint += "?" + searchParams.toString();
    }

    const headers = {
      Authorization: "Bearer " + accessToken,
      "X-Novata-App": "NOVATA",
      "novata-app": "EXCEL_ADDIN",
      "novata-organization-id": selectedOrgId,
    };

    const selectedOrgUnitId = await getSelectedOrganizationUnitId();
    if (selectedOrgUnitId) {
      headers["novata-organization-unit-id"] = selectedOrgUnitId;
    }

    if (logCfDiag) {
      logger.info(
        "[DIAG:CF:fetch:%s] Fetching: url=%s, orgId=%s, orgUnitId=%s, tokenLength=%d",
        callId,
        fullEndpoint,
        selectedOrgId,
        selectedOrgUnitId || "none",
        accessToken.length
      );
    }

    const response = await managedFetch(fullEndpoint, {
      method: "GET",
      headers,
      mode: "cors",
      credentials: "omit",
    });

    if (logCfDiag) {
      logger.info(
        "[DIAG:CF:fetch:%s] Response: status=%d, ok=%s, statusText=%s",
        callId,
        response.status,
        response.ok,
        response.statusText
      );
    }

    if (!response.ok) {
      let errorMessage = "HTTP " + response.status;
      try {
        const errorData = response.body;
        errorMessage = errorData.error || errorData.message || errorMessage;
        if (logCfDiag) {
          logger.error(
            "[DIAG:CF:fetch:%s] API error response: %s",
            callId,
            JSON.stringify(errorData).substring(0, 500)
          );
        }
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      return "#ERROR: " + errorMessage;
    }

    const result = response.body;
    let dataObject = null;

    if (result && typeof result === "object") {
      if (result.data) {
        dataObject = Array.isArray(result.data) ? result.data[0] : result.data;
      } else if (Array.isArray(result)) {
        dataObject = result[0];
      } else {
        dataObject = result;
      }
    }

    if (!dataObject) {
      if (logCfDiag) {
        logger.warn("[DIAG:CF:fetch:%s] No data object in response", callId);
      }
      return "#N/A";
    }

    const fieldNameTrimmed = fieldName.trim();
    const propertyPath = attributeMap[fieldNameTrimmed] || fieldNameTrimmed;
    const fieldValue = getNestedProperty(dataObject, propertyPath);

    if (logCfDiag) {
      logger.info(
        "[DIAG:CF:fetch:%s] OK: field=%s, path=%s, hasValue=%s",
        callId,
        fieldNameTrimmed,
        propertyPath,
        fieldValue !== undefined
      );
    }

    if (fieldValue === undefined || fieldValue === null) {
      if (postProcess) {
        const fallback = postProcess(dataObject, fieldNameTrimmed);
        if (fallback !== null && fallback !== undefined) return fallback;
      }
      return "#N/A";
    }

    return formatForExcel(fieldValue);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    if (logCfDiag) {
      logger.error("[DIAG:CF:fetch:%s] Exception: %s (stack: %s)", callId, err.message, err.stack);
    }
    return "#ERROR: " + (err.message || "Unknown error occurred");
  }
}
